import { tradingDayOf } from './report-groups'
import type { InvoiceLine, SalesRegisterRow } from './types'

/**
 * The invoice lines, in blocks — the legacy "details" reports.
 *
 * The server answers one flat list of lines and every report below is that list
 * cut a different way, as `report-groups.ts` does for the register. Money is
 * added in whole cents, so a subtotal is never a float-drift away from the
 * lines under it.
 */

/** What a block of lines is filed under, above the item. */
export const LineBlock = {
  Category: 'category',
  Location: 'location',
  Employee: 'employee',
  Customer: 'customer',
} as const

export type LineBlock = (typeof LineBlock)[keyof typeof LineBlock]

export function lineBlockName(line: InvoiceLine, block: LineBlock): string {
  switch (block) {
    case LineBlock.Category:
      return line.categoryName
    case LineBlock.Location:
      return line.branchName || 'Unknown outlet'
    case LineBlock.Employee:
      return line.cashierName || 'No cashier (QR ordering)'
    case LineBlock.Customer:
      return line.customerName || 'Guest'
  }
}

/** A line of a details report — an invoice line, a subtotal of them, or an invoice's own totals. */
export interface DetailRow {
  at: string | null
  invoiceNumber: string | null
  orderNumber: string | null
  branchName: string | null
  customerName: string | null
  cashierName: string | null
  name: string | null
  variantName: string | null
  sku: string | null
  quantity: number
  compQuantity: number
  totalAmount: number
  discount: number
  /** Before VAT and delivery on a line; what the customer owed on an invoice's own row. */
  net: number
  vat: number
  delivery: number
  /** Null when no line under it had a cost. */
  cost: number | null
  margin: number | null
  marginPercent: number | null
}

const cents = (amount: number | null | undefined) => Math.round((amount ?? 0) * 100)

export function asDetail(line: InvoiceLine): DetailRow {
  return {
    at: line.at,
    invoiceNumber: line.invoiceNumber,
    orderNumber: line.orderNumber,
    branchName: line.branchName,
    customerName: line.customerName,
    cashierName: line.cashierName,
    name: line.name,
    variantName: line.variantName,
    sku: line.sku,
    quantity: line.quantity,
    compQuantity: line.compQuantity,
    totalAmount: line.totalAmount,
    discount: line.discount,
    net: line.net,
    vat: 0,
    delivery: 0,
    cost: line.cost,
    margin: line.margin,
    marginPercent: line.margin !== null && line.net ? line.margin / line.net : null,
  }
}

/**
 * Lines added together. Margin is worked over the costed lines only, as the
 * server's item report does: a line with no cost is left out of the margin
 * rather than counted at 100%.
 */
export function sumLines(lines: InvoiceLine[]): DetailRow {
  let quantity = 0
  let comp = 0
  let total = 0
  let discount = 0
  let net = 0
  let cost = 0
  let costedNet = 0
  let costed = 0

  for (const line of lines) {
    quantity += line.quantity
    comp += line.compQuantity
    total += cents(line.totalAmount)
    discount += cents(line.discount)
    net += cents(line.net)

    if (line.cost !== null) {
      costed += 1
      cost += cents(line.cost)
      costedNet += cents(line.net)
    }
  }

  return {
    at: null,
    invoiceNumber: null,
    orderNumber: null,
    branchName: null,
    customerName: null,
    cashierName: null,
    name: null,
    variantName: null,
    sku: null,
    quantity,
    compQuantity: comp,
    totalAmount: total / 100,
    discount: discount / 100,
    net: net / 100,
    vat: 0,
    delivery: 0,
    cost: costed ? cost / 100 : null,
    margin: costed ? (costedNet - cost) / 100 : null,
    marginPercent: costed && costedNet ? (costedNet - cost) / costedNet : null,
  }
}

export interface DetailBlock {
  name: string
  /** Items in the block, biggest first. Absent for a block of invoices. */
  items: { name: string; lines: InvoiceLine[] }[]
  lines: InvoiceLine[]
}

const itemKey = (line: InvoiceLine) =>
  `${line.sku ?? ''}\u0000${line.name}\u0000${line.variantName ?? ''}`

export function itemLabel(line: InvoiceLine): string {
  return line.variantName ? `${line.name} · ${line.variantName}` : line.name
}

const netOf = (lines: InvoiceLine[]) => lines.reduce((sum, line) => sum + cents(line.net), 0)

/** Items of a run of lines, biggest first, equal items alphabetically. */
function itemsOf(lines: InvoiceLine[]) {
  const items = new Map<string, InvoiceLine[]>()

  for (const line of lines) items.set(itemKey(line), [...(items.get(itemKey(line)) ?? []), line])

  return [...items.values()]
    .map((itemLines) => ({ name: itemLabel(itemLines[0]), lines: itemLines }))
    .sort((a, b) => netOf(b.lines) - netOf(a.lines) || a.name.localeCompare(b.name))
}

/**
 * Lines as blocks of category, outlet, cashier or customer, each holding its
 * items and, under each item, the invoices it sold on. `block` null is the items
 * alone, with no block above them.
 */
export function blockLines(lines: InvoiceLine[], block: LineBlock | null): DetailBlock[] {
  if (!block) return [{ name: '', items: itemsOf(lines), lines }]

  const blocks = new Map<string, InvoiceLine[]>()

  for (const line of lines) {
    const name = lineBlockName(line, block)

    blocks.set(name, [...(blocks.get(name) ?? []), line])
  }

  return [...blocks.entries()]
    .map(([name, blockLines]) => ({ name, items: itemsOf(blockLines), lines: blockLines }))
    .sort((a, b) => netOf(b.lines) - netOf(a.lines) || a.name.localeCompare(b.name))
}

/** Whether a line mentions every word of a search, for the details reports. */
export function lineSearchFields(line: InvoiceLine) {
  return [
    line.invoiceNumber,
    line.orderNumber,
    line.name,
    line.variantName,
    line.sku,
    line.customerName,
    line.cashierName,
    line.branchName,
    line.categoryName,
  ]
}

/* -------------------------------------------------------------------------- */
/* Sales by channel                                                           */
/* -------------------------------------------------------------------------- */

export interface ChannelWeek {
  /** The Monday the week starts on, `YYYY-MM-DD`. Null on the total row. */
  week: string | null
  posInvoices: number
  posNet: number
  qrInvoices: number
  qrNet: number
  invoices: number
  net: number
  /** QR ordering's share of net, 0–1. */
  qrShare: number
}

const POS_CHANNEL = 'POS'

/** The Monday of the week a trading day falls in. */
export function weekStart(day: string): string {
  const [year, month, date] = day.split('-').map(Number)
  const at = new Date(Date.UTC(year, month - 1, date))
  const sinceMonday = (at.getUTCDay() + 6) % 7

  at.setUTCDate(at.getUTCDate() - sinceMonday)

  return at.toISOString().slice(0, 10)
}

interface ChannelTally {
  posInvoices: number
  posNet: number
  qrInvoices: number
  qrNet: number
}

function asWeek(week: string | null, tally: ChannelTally): ChannelWeek {
  const net = tally.posNet + tally.qrNet

  return {
    week,
    posInvoices: tally.posInvoices,
    posNet: tally.posNet / 100,
    qrInvoices: tally.qrInvoices,
    qrNet: tally.qrNet / 100,
    invoices: tally.posInvoices + tally.qrInvoices,
    net: net / 100,
    qrShare: net ? tally.qrNet / net : 0,
  }
}

/**
 * The register's sales laid out by week, POS beside QR ordering.
 *
 * Refunds are left out, as the server leaves them out of every sale total, so
 * the total row agrees with the sales summary for the same period.
 */
export function channelWeeks(rows: SalesRegisterRow[]): {
  weeks: ChannelWeek[]
  total: ChannelWeek
} {
  const weeks = new Map<string, ChannelTally>()
  const all: ChannelTally = { posInvoices: 0, posNet: 0, qrInvoices: 0, qrNet: 0 }

  for (const row of rows) {
    if (row.kind !== 'sale') continue

    const week = weekStart(tradingDayOf(row.at))
    const tally = weeks.get(week) ?? { posInvoices: 0, posNet: 0, qrInvoices: 0, qrNet: 0 }
    const isPos = row.channel === POS_CHANNEL

    for (const t of [tally, all]) {
      if (isPos) {
        t.posInvoices += 1
        t.posNet += cents(row.net)
      } else {
        t.qrInvoices += 1
        t.qrNet += cents(row.net)
      }
    }

    weeks.set(week, tally)
  }

  return {
    weeks: [...weeks.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([week, tally]) => asWeek(week, tally)),
    total: asWeek(null, all),
  }
}
